import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Menu,
  MenuItem,
  IconButton,
  Divider,
} from '@mui/material';
import {
  FolderOutlined as FilesIcon,
  Logout as LogoutIcon,
  PeopleOutlined as PatientsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { markExplicitLogoutRedirectDefault } from '../utils/authRedirect';
import { ACCOUNT_TABS, getAccountTabPath } from '../constants/accountTabs';
import UserAvatar from './UserAvatar';
import { SIMULATION_SETUP_PATH } from '../constants/clientRoutes';
import { userHasMyHospitalModule } from '../utils/moduleAccess';

const Navbar = () => {
  const navigate = useNavigate();
  const { user, isGuest, logout } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleLogout = () => {
    markExplicitLogoutRedirectDefault();
    logout();
    setAnchorEl(null);
    navigate('/login', { replace: true });
  };

  const handleUserMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAccountTab = (tabId) => {
    handleClose();
    navigate(getAccountTabPath(tabId));
  };

  const handleMyPatients = () => {
    handleClose();
    navigate('/my-patients');
  };

  const handleMyFiles = () => {
    handleClose();
    navigate('/my-files');
  };

  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim()
    || user?.first_name
    || 'User';

  return (
    <AppBar position="sticky" elevation={1}>
      <Toolbar sx={{ px: { xs: 2, md: 4 }, py: 1 }}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, cursor: 'pointer' }}
          onClick={() => navigate(SIMULATION_SETUP_PATH)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') navigate(SIMULATION_SETUP_PATH);
          }}
        >
          <Box
            component="img"
            src="/images/logo.png"
            alt="Clinical Theater"
            sx={{
              width: 40,
              height: 40,
              mr: 2,
              borderRadius: 1,
              objectFit: 'contain',
            }}
          />
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              display: { xs: 'none', sm: 'block' },
            }}
          >
            Clinical Theater
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {!isGuest && (
            <>
              <IconButton
                onClick={handleUserMenu}
                aria-label="Account menu"
                sx={{
                  p: 0.375,
                  border: '2px solid transparent',
                  '&:hover': {
                    borderColor: 'primary.200',
                  },
                }}
              >
                <UserAvatar
                  firstName={user?.firstName || user?.first_name}
                  lastName={user?.lastName || user?.last_name}
                  email={user?.email}
                  avatarColor={user?.avatarColor}
                  size={36}
                />
              </IconButton>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                PaperProps={{
                  elevation: 3,
                  sx: {
                    mt: 1.5,
                    minWidth: 260,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                  },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <UserAvatar
                    firstName={user?.firstName || user?.first_name}
                    lastName={user?.lastName || user?.last_name}
                    email={user?.email}
                    avatarColor={user?.avatarColor}
                    size={40}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" fontWeight={600} noWrap>
                      {displayName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {user?.email}
                    </Typography>
                  </Box>
                </Box>

                <Divider />

                {ACCOUNT_TABS.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <MenuItem
                      key={tab.id}
                      onClick={() => handleAccountTab(tab.id)}
                      sx={{ gap: 1.5, py: 1.25 }}
                    >
                      <Icon fontSize="small" />
                      {tab.label}
                    </MenuItem>
                  );
                })}

                {userHasMyHospitalModule(user) && (
                  <>
                    <MenuItem
                      onClick={handleMyPatients}
                      sx={{ gap: 1.5, py: 1.25 }}
                    >
                      <PatientsIcon fontSize="small" />
                      My Patients
                    </MenuItem>
                    <MenuItem
                      onClick={handleMyFiles}
                      sx={{ gap: 1.5, py: 1.25 }}
                    >
                      <FilesIcon fontSize="small" />
                      My Files
                    </MenuItem>
                  </>
                )}

                <Divider />

                <MenuItem
                  onClick={handleLogout}
                  sx={{
                    gap: 1.5,
                    py: 1.25,
                    color: 'error.main',
                    '&:hover': {
                      bgcolor: 'error.light',
                      color: 'error.dark',
                    },
                  }}
                >
                  <LogoutIcon fontSize="small" />
                  Sign Out
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
